/**
 * RESO Auth Check
 *
 * Probes the RESO `/refresh` endpoint to verify a valid session cookie.
 * If the session is missing (401), redirects to RESO's `/redirect`
 * endpoint which authenticates and redirects back to the app.
 *
 * In debug mode (`RESO_DEBUG_MODE`), failures are bypassed so that
 * local development can proceed without a RESO server.
 */

const SESSION_STORAGE_KEY = 'reso_auth_in_progress';

/**
 * Creates a session check function for RESO.
 *
 * @param baseUrl   - The RESO service base URL (from tenant config `RESO_BASE_URL`)
 * @param debugMode - When true, failures are logged but treated as success
 * @returns A function that returns true if authenticated, false if a redirect was initiated
 */
export function createResoCheck(
  baseUrl: string,
  debugMode: boolean = false,
): () => Promise<boolean> {
  return async (): Promise<boolean> => {
    try {
      const response = await fetch(`${baseUrl}/refresh`, {
        credentials: 'include',
      });

      if (response.status === 401) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
        window.location.href = `${baseUrl}/redirect?redirectLink=${encodeURIComponent(window.location.href)}`;
        return false;
      }

      if (!response.ok) {
        throw new Error('RESO session refresh failed');
      }

      return true;
    } catch (error) {
      if (debugMode) {
        console.warn(
          'RESO refresh failed in debug mode — continuing anyway',
          error,
        );
        return true;
      }
      throw error;
    }
  };
}

export { SESSION_STORAGE_KEY as RESO_SESSION_STORAGE_KEY };
